import React, { useState, useEffect } from 'react';
import { Home, Plus, Search, ArrowRight } from 'lucide-react';
import { useCurrency } from '../../hooks/useCurrency';
import { supabase } from '../../lib/supabase';

interface BalanceTransfer {
  id: string;
  transfer_date: string;
  from_account: string;
  to_account: string;
  amount: number;
  reference_number: string;
  description?: string;
  status: string;
  created_by: string;
}

export default function BalanceTransfer() {
  const { formatCurrency } = useCurrency();
  const [transfers, setTransfers] = useState<BalanceTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    from_account: '',
    to_account: '',
    amount: '',
    description: '',
  });

  useEffect(() => {
    loadBalanceTransfers();
  }, []);

  const loadBalanceTransfers = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setTransfers(getMockBalanceTransfers());
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('balance_transfers')
        .select('*')
        .order('transfer_date', { ascending: false });

      if (error) throw error;
      setTransfers(data || []);
    } catch (error) {
      console.error('Error loading balance transfers:', error);
      setTransfers(getMockBalanceTransfers());
    } finally {
      setLoading(false);
    }
  };

  const getMockBalanceTransfers = (): BalanceTransfer[] => {
    return [
      {
        id: '1',
        transfer_date: '2025-01-27',
        from_account: 'Main Cash Account',
        to_account: 'Petty Cash Account',
        amount: 50000,
        reference_number: 'BT-2025-001',
        description: 'Monthly petty cash allocation',
        status: 'completed',
        created_by: 'Admin',
      },
      {
        id: '2',
        transfer_date: '2025-01-26',
        from_account: 'Bank Account - ABC Bank',
        to_account: 'Main Cash Account',
        amount: 200000,
        reference_number: 'BT-2025-002',
        description: 'Cash withdrawal for operations',
        status: 'completed',
        created_by: 'Admin',
      },
      {
        id: '3',
        transfer_date: '2025-01-25',
        from_account: 'Main Cash Account',
        to_account: 'Bank Account - XYZ Bank',
        amount: 150000,
        reference_number: 'BT-2025-003',
        description: 'Deposit to savings account',
        status: 'pending',
        created_by: 'Admin',
      },
    ];
  };

  const filteredTransfers = transfers.filter(transfer => {
    const matchesSearch = 
      transfer.from_account.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transfer.to_account.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transfer.reference_number.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalTransferred = filteredTransfers.reduce((sum, transfer) => sum + transfer.amount, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        alert('Balance transfer created successfully (mock mode)');
        setShowForm(false);
        setFormData({
          from_account: '',
          to_account: '',
          amount: '',
          description: '',
        });
        return;
      }

      const { error } = await supabase
        .from('balance_transfers')
        .insert([{
          ...formData,
          transfer_date: new Date().toISOString().split('T')[0],
          status: 'pending',
        }]);
      
      if (error) throw error;

      await loadBalanceTransfers();
      setShowForm(false);
      setFormData({
        from_account: '',
        to_account: '',
        amount: '',
        description: '',
      });
    } catch (error) {
      console.error('Error creating balance transfer:', error);
      alert('Error creating balance transfer');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Balance Transfer</span>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New Transfer</span>
          </button>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        {showForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create Balance Transfer</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Account *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.from_account}
                    onChange={(e) => setFormData({ ...formData, from_account: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Account *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.to_account}
                    onChange={(e) => setFormData({ ...formData, to_account: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount *
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Create Transfer
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Transfers</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{filteredTransfers.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Amount Transferred</p>
            <p className="text-2xl font-bold text-teal-600 mt-1">{formatCurrency(totalTransferred)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search transfers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    From Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    To Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredTransfers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No balance transfers found
                    </td>
                  </tr>
                ) : (
                  filteredTransfers.map((transfer) => (
                    <tr key={transfer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(transfer.transfer_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transfer.from_account}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center space-x-2">
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                          <span>{transfer.to_account}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-teal-600 font-bold">
                        {formatCurrency(transfer.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {transfer.reference_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          transfer.status === 'completed' ? 'bg-green-100 text-green-800' :
                          transfer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {transfer.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
